import React, { useState, useRef, useEffect,useCallback, useMemo,useLayoutEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Row,
  Col,
  Card,
  Breadcrumb,
  Badge,
  Empty,
  notification,
} from "antd";
import {
  PlusOutlined,
  HomeOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  UnlockOutlined,
  LockOutlined
} from "@ant-design/icons";
import { decodeToken } from "react-jwt";
import Create from "./Create";
import UserAPI from "../../../restAPI/UserAPI";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Resizable } from 'react-resizable'; 
import Edit from "./Edit";
import View from "./View";

const ResizableTitle = (props) => {
  const { onResize, width, children, ...restProps } = props;
  if (!width) return <th {...restProps}>{children}</th>;

  return (
    <th {...restProps}>
      <Resizable
        width={width}
        height={0}
        onResize={onResize}
        draggableOpts={{ enableUserSelectHack: false }}
        handle={
          <span
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              cursor: "col-resize",
              width: "2px",
              height: "100%",
              background: "#ffeb3b3b",
            }}
          />
        }
      >
        <div>{children}</div>
      </Resizable>
    </th>
  );
};

export default function UserTable() {
  const tableContainerRef = useRef();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const createRef = useRef(null);
  const editRef = useRef();
  const viewRef = useRef();
  const { token, role } = useSelector((state) => state.auth);
  const [allUsersForDisplay, setAllUsersForDisplay] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const { groupId, companyId } = useOutletContext();
  const notificationShown = useRef(false);
  const resolvedRoleName = useMemo(() => {
    if (role) {
      if (typeof role === "string") {
        return role;
      }
      if (Array.isArray(role) && role.length > 0) {
        return role[0];
      }
      if (typeof role === "object" && role.name) {
        return role.name;
      }
    }

    if (!token) {
      return null;
    }

    try {
      const decoded = decodeToken(token);
      if (decoded?.role?.name) {
        return decoded.role.name;
      }
      if (Array.isArray(decoded?.role) && decoded.role.length > 0) {
        return decoded.role[0];
      }
      if (typeof decoded?.role === "string") {
        return decoded.role;
      }
    } catch (error) {
      return null;
    }

    return null;
  }, [token, role]);

  const canUnlockUsers = useMemo(() => {
    const normalized = (resolvedRoleName || "").toLowerCase();
    return normalized === "system admin" || normalized === "group admin";
  }, [resolvedRoleName]);

  const openNotificationWithIcon = (type, message, description, placement) => {
    if (!notificationShown.current) {
      notification[type]({
        message: message,
        description: description,
        placement,
      });
      notificationShown.current = true;
      setTimeout(() => {
        notificationShown.current = false;
      }, 5000);
    }
  };

  // Open the Drawer in the Create component
  const showChildDrawer = () => {
    if (createRef.current) {
      createRef.current.openDrawer(); // Call the openDrawer method in Create
    }
  };

  // Column search functionality
  const getColumnSearchProps = (dataIndex,placeholder) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${placeholder}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    setSearchText("");
    clearFilters();
    confirm();
  };

  const initialColumns = [
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
      ...getColumnSearchProps("firstName","First Name"),
      percentageWidth: 0.20,
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
      ...getColumnSearchProps("lastName","Last Name"),
      percentageWidth: 0.20,
    },
    {
      title: "User Type",
      dataIndex: "usertype",
      key: "usertype",
      sorter: (a, b) => a.usertype.localeCompare(b.usertype),
      percentageWidth: 0.15,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email","Email"),
      percentageWidth: 0.20,
    },
    {
      title: "Status",
      percentageWidth: 0.15,

      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (_, record) => {
        const isLocked = Boolean(record.isLocked);
        const statusText = isLocked ? "Locked" : record.status;
        const badgeStatus = isLocked
          ? "error"
          : statusText === "Active"
            ? "success"
            : statusText === "Inactive"
              ? "default"
              : "processing";

        return (
          <Space size={6} align="center">
            <Badge status={badgeStatus} />
            <span>{statusText}</span>
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      percentageWidth: 0.10,
      align: "center",
      render: (_, record) => (
        <Space
          size="small"
          style={{ display: "flex", justifyContent: "center",gap:'revert-layer' }}
        >
          <div>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={(e) => onViewChange(record._id)}
          />
          </div>
          <div>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => onEditChange(record._id)}
          />
          </div>
          <div>
          {canUnlockUsers && record.isLocked && (
            <Button
              icon={<UnlockOutlined />}
              size="small"
              onClick={() => unlockUser(record._id)}
            />
          )}
          </div>

          
        </Space>
      ),
    },
  ];

    
    // Compute an initial container width using window.innerWidth as fallback.
    const initialContainerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

    // Set initial column widths based on the fallback container width.
    const [columns, setColumns] = useState(
      initialColumns.map(col => ({
        ...col,
        width: Math.floor(initialContainerWidth * col.percentageWidth),
      }))
    );
  
    // Function to update column widths based on the measured container.
    const updateColumnWidths = useCallback(() => {
      if (tableContainerRef.current) {
        const containerWidth = tableContainerRef.current.offsetWidth;
        setColumns(initialColumns.map(col => ({
          ...col,
          width: Math.floor(containerWidth * col.percentageWidth),
        })));
      }
    }, [initialColumns]);
  
    // Use useLayoutEffect to update column widths as soon as the component is mounted.
    useLayoutEffect(() => {
      updateColumnWidths();
    }, [updateColumnWidths]);
  
    // Update widths on window resize.
    useEffect(() => {
      window.addEventListener('resize', updateColumnWidths);
      return () => window.removeEventListener('resize', updateColumnWidths);
    }, [updateColumnWidths]);
  
  
  const handleResize = useCallback(
      (index) => (e, { size }) => {
        setColumns((prevColumns) => {
          const newColumns = [...prevColumns];
          newColumns[index] = { ...newColumns[index], width: size.width };
          return newColumns;
        });
      },
      []
    );
    
    const mergedColumns = useMemo(
      () =>
        columns.map((col, index) => ({
          ...col,
          onHeaderCell: (column) => ({
            width: column.width,
            onResize: handleResize(index),
          }),
        })),
      [columns, handleResize]
    );
    

  const onEditChange = (id) => {
    if (editRef.current) {
      editRef.id = id;
      editRef.current.openEditDrawer(); // Call the openDrawer method in Edit
    }
  };

  const onViewChange = (id) => {
    if (viewRef.current) {
      viewRef.id = id;
      viewRef.current.openViewDrawer(); // Call the openDrawer method in Edit
    }
  };

  const unlockUser = async (userId) =>{
    if(userId !=null)
    {
      await UserAPI.unlockUser(
        token,
        localStorage.getItem("groupId"),
        localStorage.getItem("companyId"),
        userId
      )
       .then((response) => {
        getMembersForLoggedUser();
          openNotificationWithIcon(
            "success",
            "User unlock",
            `${response.message}`,
            "bottomLeft"
          );
        })
        .catch((err) => {
          if (err.response) {
            openNotificationWithIcon(
              "error",
              "User unlock",
              `${err.response.data.message}`,
              "bottomLeft"
            );
          } else {
            openNotificationWithIcon(
              "error",
              "User unlock",
              `${err.message}`,
              "bottomLeft"
            );
          }
        });
    }
  }

  useEffect(() => {
    getMembersForLoggedUser();
  }, [groupId, companyId]);

  // Refresh the table
  const refreshTable = () => {
    // Placeholder for actual data refresh logic
    getMembersForLoggedUser();
  };

  const getMembersForLoggedUser = async () => {
    await UserAPI.getMembersForLoggedUser(
      token,
      localStorage.getItem("groupId"),
      localStorage.getItem("companyId")
    )
      .then((res) => {
        if (res?.data) {
          //console.log("userdetails",JSON.stringify(res.data));
          const rows = [];
          res.data.map((rowValue) => {
            let statusValue = null;
            if (rowValue.status) {
              statusValue = "Active";
            } else {
              statusValue = "Inactive";
            }
            if(rowValue.isLocked)
            {
              statusValue = "Locked";
            }

            rows.push({
              _id: rowValue._id,
              firstName: rowValue.firstName,
              lastName: rowValue.lastName,
              usertype: rowValue.role.name,
              email: rowValue.email,
              status: statusValue,
              isLocked:rowValue.isLocked,
            });
          });
          setAllUsersForDisplay(rows);
          setAllUsers(res.data);
        }
      })
      .catch((err) => {
        console.log("error getting user data :", err.message);
      });
  };

  return (
    <Row>
      <Col md={24}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item href="/dashboard">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Administration</Breadcrumb.Item>
          <Breadcrumb.Item>Users</Breadcrumb.Item>
        </Breadcrumb>
        <div className="border-container">
          <Button
            type="primary"
            disabled={localStorage.getItem("groupId") === null}
            variant="dashed"
            className="card-button"
            size="large"
            icon={<PlusOutlined />}
            onClick={showChildDrawer}
            shape="circle"
          ></Button>
          <Card title="Users" bordered={true}>
          <div
              style={{
                maxWidth: '100%', // Ensure the container doesn't exceed its width
                overflowX: 'auto', // Enable horizontal scroll
              }}
            >
            <Table size="middle" components={{
                header: {
                  cell: ResizableTitle, 
                },
              }}
              columns={mergedColumns} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No records found!'/> }}
               dataSource={allUsersForDisplay} />
               </div>
          </Card>
        </div>
        <Create ref={createRef} onRefresh={refreshTable} />
        <Edit ref={editRef} onRefresh={refreshTable} />
        <View ref={viewRef} onRefresh={refreshTable} />
      </Col>
    </Row>
  );
}
